import { Link } from "react-router-dom";
import { CalendarDays, TrendingUp, ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/content";

const FOR = ["Swimmers who are so close to going solo", "Confident beginners needing a final push", "Children ready to swim independently", "Swimmers rebuilding momentum", "Faster progress before a trip, camp or event", "Children who need consistency without long breaks"];

export default function Intensive() {
  return (
    <div data-testid="intensive-page">
      <PageHeader
        overline="Swim Breakthrough Intensive"
        title="Five days."
        italic="One breakthrough."
        subtitle="An additional program for swimmers who are so close to swimming on their own — five consecutive days to push them over the finish line to independent swimming."
        image={IMAGES.sunsetSplash}
        imagePosition="center 22%"
      />

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="overline mb-4" style={{ color: "var(--gold)" }}>Who it's for</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight mb-8" style={{ color: "var(--navy)" }}>
                Designed for swimmers who are <span className="italic">so close.</span>
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-3">
              {FOR.map((f, i) => (
                <Reveal key={f} delay={i * 0.05}>
                  <div className="p-5 rounded-xl bg-white border font-body" style={{ borderColor: "rgba(212,175,55,0.25)", color: "var(--navy)" }}>{f}</div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="rounded-3xl overflow-hidden border mb-8" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                <img src={IMAGES.breakthroughKit} alt="The Mimi & Me Swim Breakthrough Intensive kit" className="w-full h-[460px] object-cover object-top" />
              </div>
              <div className="p-8 rounded-2xl" style={{ backgroundColor: "var(--navy)" }}>
                <div className="flex items-center gap-4 mb-4">
                  <CalendarDays size={28} style={{ color: "var(--gold)" }} />
                  <span className="font-display text-3xl" style={{ color: "var(--cream)" }}>5 × 20-minute lessons</span>
                </div>
                <p className="font-display text-5xl" style={{ color: "var(--gold)" }}>$585</p>
                <p className="font-body font-light mt-3" style={{ color: "var(--powder)" }}>
                  Five consecutive days. An additional program for swimmers who are so close to
                  swimming individually — the final push to independence. Ongoing lessons two or
                  three times per week may be recommended afterward.
                </p>
                <Link to="/contact" className="btn-primary mt-6 !bg-[var(--gold)] !text-[var(--navy)]" data-testid="intensive-reserve">Reserve a Breakthrough <ArrowUpRight size={16} /></Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: "var(--sand)" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <TrendingUp size={34} style={{ color: "var(--gold)" }} className="mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
              Why consistency matters
            </h2>
            <p className="font-body font-light text-lg mt-6" style={{ color: "var(--muted)" }}>
              Every new swimmer begins with five consecutive days in the first week to build trust
              and momentum, then drops to two or three lessons per week as recommended. Consecutive
              days prevent children from having to restart emotionally and physically each week —
              helping them retain skills and progress far faster.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>See it in action</p>
            <h2 className="font-display text-3xl md:text-5xl leading-tight mb-10" style={{ color: "var(--navy)" }}>
              What a <span className="italic">breakthrough</span> looks like
            </h2>
            <div className="relative rounded-[2rem] overflow-hidden border shadow-[0_30px_80px_rgba(11,27,61,0.2)]" style={{ borderColor: "rgba(212,175,55,0.4)" }}>
              <video
                className="w-full h-auto block"
                src="/videos/swim-breakthrough.mp4"
                poster="/videos/swim-breakthrough.jpg"
                controls
                playsInline
                preload="none"
                data-testid="breakthrough-video"
              />
            </div>
            <div className="mt-10">
              <Link to="/contact" className="btn-primary" data-testid="breakthrough-video-cta">
                Reserve a Breakthrough <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
